import React, { useState } from "react";
import { FinancialDocument } from "@common/types/financialDocument";
import { TransactionListItem } from "@common/schemas/transaction.schema";
import { useIsDateLocked } from "@hooks/useIsDateLocked";
import { toDisplayDateOnly } from "@helpers/date";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { TransactionAmount } from "../../../transactions/TransactionAmount";
import { TransactionDateColumnContent } from "../../../transactions/ListItem/TransactionDateColumnContent";
import { TransactionLockColumnContent } from "../../../transactions/ListItem/TransactionLockColumnContent";
import { TransactionAccountColumnContent } from "../../../transactions/ListItem/TransactionAccountColumnContent";
import { TransactionDescriptionColumnContent } from "../../../transactions/ListItem/TransactionDescriptionColumnContent";

interface Props {
    transaction: TransactionListItem;
    financialDocument: FinancialDocument;
    onMatch(): Promise<unknown>;
}

export const MatchingViewTransaction: React.FC<Props> = ({
    transaction,
    financialDocument,
    onMatch,
}) => {
    const [loading, setLoading] = useState(false);
    const isLocked = useIsDateLocked(
        transaction.entity.id,
        toDisplayDateOnly(transaction.dateOnly ?? transaction.date),
    );

    const alreadyLinkedToCurrentFinancialDocument = (
        transaction.transactionMatches ?? []
    ).some((tm) => tm.financialDocument?.id === financialDocument.id);

    return (
        <tr data-testid="transaction-row">
            <td data-testid="transaction-cell-lock">
                <TransactionLockColumnContent isLocked={isLocked} />
            </td>
            <td data-testid="transaction-cell-date">
                <TransactionDateColumnContent transaction={transaction} />
            </td>
            <td data-testid="transaction-cell-description">
                <TransactionDescriptionColumnContent
                    transaction={transaction}
                />
            </td>
            <td className="text-right" data-testid="transaction-cell-account">
                <TransactionAccountColumnContent transaction={transaction} />
            </td>
            <td className="text-right" data-testid="transaction-cell-amount">
                <TransactionAmount transaction={transaction} addSign color />
            </td>
            <td>
                <EntityAvatar entity={transaction.entity} size="xs" />
            </td>
            <td data-testid="transaction-cell-link">
                <ButtonWithLoader
                    size="sm"
                    variant="primary"
                    disabled={alreadyLinkedToCurrentFinancialDocument}
                    onClick={async () => {
                        await onMatch();
                    }}
                    loading={loading}
                    setLoading={setLoading}
                >
                    {alreadyLinkedToCurrentFinancialDocument
                        ? "Linked"
                        : "Link"}
                </ButtonWithLoader>
            </td>
        </tr>
    );
};
